import { useRef, useState, useCallback, useEffect } from "react";
import { Star, Trash2, Upload, X, ZoomIn, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Thumbnail } from "@/components/shared/Thumbnail";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useEntityImages,
  useUploadEntityImage,
  useSetPrimaryImage,
  useDeleteEntityImage,
  type EntityType,
  type EntityImage,
} from "@/hooks/useEntityImages";

// ── Lightbox ─────────────────────────────────────────────────────────────

function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: EntityImage[];
  startIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);

  const go = (delta: number) => {
    setIndex((i) => (i + delta + images.length) % images.length);
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/90" style={{ zIndex: 9999 }} />
        <Dialog.Content
          className="fixed inset-0 flex items-center justify-center outline-none"
          style={{ zIndex: 9999 }}
          onPointerDownOutside={onClose}
        >
          <Dialog.Close asChild>
            <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10">
              <X className="h-7 w-7" />
            </button>
          </Dialog.Close>

          {images.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl px-3 z-10"
                onClick={() => go(-1)}
              >
                ‹
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl px-3 z-10"
                onClick={() => go(1)}
              >
                ›
              </button>
            </>
          )}

          <img
            src={images[index].public_url}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"
          />

          {images.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {index + 1} / {images.length}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── ImageGallery ─────────────────────────────────────────────────────────

interface ImageGalleryProps {
  entityType: EntityType;
  entityId: string;
  orgId: string;
}

export function ImageGallery({ entityType, entityId, orgId }: ImageGalleryProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dragOverZoneRef = useRef(false); // tracks hover state synchronously; Safari drop event has clientX/Y=0
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { data: images = [], isLoading } = useEntityImages(entityType, entityId);
  const upload = useUploadEntityImage(entityType);
  const setPrimary = useSetPrimaryImage(entityType);
  const deleteImage = useDeleteEntityImage(entityType);

  const uploadFiles = useCallback((files: File[]) => {
    files
      .filter((f) => f.type.startsWith("image/"))
      .forEach((file) => upload.mutate({ file, entityId, orgId }));
  }, [upload, entityId, orgId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  // Use native DOM listeners instead of React synthetic events — Radix portals
  // can silently drop drag events before they bubble through the React tree.
  // Document-level drag handlers with coordinate check — avoids Radix portal
  // event targeting issues in Safari where element-level drop never fires.
  useEffect(() => {
    const el = dropZoneRef.current;
    if (!el) return;

    const isOver = (e: DragEvent) => {
      const r = el.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    };

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      const over = isOver(e);
      dragOverZoneRef.current = over;
      if (over) {
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        setIsDraggingOver(true);
      } else {
        setIsDraggingOver(false);
      }
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      // Use ref instead of isOver(e) — Safari fires drop with clientX/Y=0
      const wasOver = dragOverZoneRef.current;
      dragOverZoneRef.current = false;
      setIsDraggingOver(false);
      if (!wasOver) return;
      const dt = e.dataTransfer;
      const files: File[] = dt?.items
        ? Array.from(dt.items).filter(i => i.kind === "file").map(i => i.getAsFile()).filter((f): f is File => f !== null)
        : dt?.files ? Array.from(dt.files) : [];
      if (files.length) uploadFiles(files);
    };

    const onDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) setIsDraggingOver(false);
    };

    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    document.addEventListener("dragleave", onDragLeave);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
      document.removeEventListener("dragleave", onDragLeave);
    };
  }, [uploadFiles]);

  if (isLoading) {
    return <div className="h-20 flex items-center justify-center text-sm text-muted-foreground">Loading images…</div>;
  }

  return (
    <div className="space-y-3">
      {/* Grid of images */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((img, i) => (
            <div
              key={img.id}
              className="relative group aspect-square rounded-md overflow-hidden border border-border bg-muted"
            >
              <Thumbnail
                url={img.public_url}
                size={300}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightboxIndex(i)}
              />

              {img.is_primary && (
                <div className="absolute top-1 left-1 bg-amber-400 text-amber-900 rounded-full p-0.5">
                  <Star className="h-3 w-3 fill-current" />
                </div>
              )}

              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Center: View */}
                <div className="flex items-center justify-center h-full">
                  <button
                    className="flex items-center gap-1.5 text-white text-sm bg-white/20 hover:bg-white/35 rounded-md px-4 py-2"
                    onClick={() => setLightboxIndex(i)}
                  >
                    <ZoomIn className="h-4 w-4" /> View
                  </button>
                </div>

                {/* Bottom row: Set Main (left) + Delete (right) */}
                <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between">
                  {!img.is_primary && (
                    <button
                      className="flex items-center gap-1 text-amber-300 text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-1"
                      onClick={() => setPrimary.mutate({ imageId: img.id, entityId, publicUrl: img.public_url })}
                      disabled={setPrimary.isPending}
                    >
                      <Star className="h-3 w-3" /> Set main
                    </button>
                  )}
                  <div className="ml-auto">
                  {confirmDeleteId === img.id ? (
                    <div className="flex items-center gap-1 bg-black/70 rounded px-1.5 py-1">
                      <AlertTriangle className="h-3 w-3 text-red-400" />
                      <span className="text-red-300 text-xs">Sure?</span>
                      <button
                        className="text-red-300 text-xs hover:text-red-100 font-medium ml-1"
                        onClick={(e) => { e.stopPropagation(); deleteImage.mutate({ image: img, entityId, allImages: images }); setConfirmDeleteId(null); }}
                        disabled={deleteImage.isPending}
                      >
                        Yes
                      </button>
                      <button
                        className="text-white/60 text-xs hover:text-white ml-1"
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      className="text-red-300/70 hover:text-red-300 p-1"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(img.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone — compact row when images exist, taller empty state */}
      <div
        ref={dropZoneRef}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex rounded-md border-2 border-dashed transition-colors cursor-pointer select-none",
          images.length === 0
            ? "flex-col items-center justify-center gap-2 py-5"
            : "flex-row items-center justify-center gap-2 py-2 px-3",
          isDraggingOver
            ? "border-primary bg-primary/5 text-primary"
            : "border-border text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <Upload className={cn("h-4 w-4 shrink-0", upload.isPending && "animate-pulse")} />
        <p className={images.length === 0 ? "text-sm" : "text-xs"}>
          {upload.isPending
            ? "Uploading…"
            : isDraggingOver
            ? "Drop to upload"
            : images.length === 0
            ? "Drag & drop images here, or click to browse"
            : "Add more photos"}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {lightboxIndex !== null && images.length > 0 && (
        <Lightbox
          images={images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
